import { useQuery } from "@apollo/client";
import { useI18n } from "react-simple-i18n";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Box, Typography, Skeleton } from "@mui/material";
import SendIcon from '@mui/icons-material/Send';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArticleIcon from '@mui/icons-material/Article';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';

import { toastMessage } from "../../utils";
import { restApi } from "../../provider/restApi";
import { useGlobalContext } from "../../provider";
import { Layout } from "../../components/layout/layout";
import { TextField } from "../../components/inputs/textfield";
import { GlobalFirstSection, GlobalSpacing } from "../../components/layout/styles";
import { FirstButton } from "../../components/buttons/buttons";
import { apiNotification } from "../../services/apiNotification";
import { GET_DocumentDetail } from "../../components/graphql";
import { PageNotFound } from ".././404";
import { DocumentPayment } from "./documentPayment";
import { config } from "../../config/config";


const emptyArray = new Array(6).fill(0);
const stripePromise = loadStripe(config.stripe.publishkey);


const PDFDetail = () => {
	const { t } = useI18n();
	const params = useParams(); 
	const [openSubscribeModal, setOpenSubscribeModal] = useState(false);

	const { data: courseData, loading, error } = useQuery(GET_DocumentDetail, {
		variables: { id: params.id || '' },
		pollInterval: 5000,
	});

	const courseDetailData: DocumentDetailObject = useMemo(() => {
		if (!!loading || !!error || !params.id || !courseData) return null;
		return courseData.getDocumentData;
	}, [courseData, loading, error, params.id]);
	
	// Assuming email is stored in localStorage
	const userId=localStorage.getItem("UserId");
	// Check if the user is in the purchasedUsers list
	const isUserPurchased = courseDetailData?.purchasedUsers?.includes(userId || '');

	if (!!error || !params.id) {
		return (
			<PageNotFound />
		)
	}

	return (
		<Layout>
			{!!loading && (
				<CourseDetailsWrapper>
					<HeroSection>
						<GlobalSpacing>
							<HeroContent>
								<Skeleton variant="rectangular" height={25} />
								<Skeleton variant="rectangular" height={50} />

								<CourseStats>
									<Skeleton variant="rounded" height={25} width={100} />
									<Skeleton variant="rounded" height={25} width={100} />
									<Skeleton variant="rounded" height={25} width={100} />
								</CourseStats>
							</HeroContent>
						</GlobalSpacing>
					</HeroSection>

					<GlobalSpacing>
						<ContentWrapper>
							<MainContent>
								<CourseSection>
									<Skeleton variant="rectangular" height={25} width={200} />
									<Skeleton variant="rectangular" height={50} />
								</CourseSection>

								<CourseSection>
									<Skeleton variant="rectangular" height={25} width={200} />

									<LearningPoints>
										{emptyArray.map((i, key: number) => (
											<LearningPoint key={key}>
												<Skeleton variant="rectangular" height={20} width="100%" />
											</LearningPoint>
										))}
									</LearningPoints>
								</CourseSection>
							</MainContent>
						</ContentWrapper>
					</GlobalSpacing>
				</CourseDetailsWrapper>
			)}

			{!loading && courseDetailData && (
				<React.Fragment>
					<CourseDetailsWrapper>
						<HeroSection>
							<GlobalSpacing>
								<HeroContent>
									<Typography variant="h3" component="h1">
										{courseDetailData.title} 
									</Typography>

									<Typography variant="subtitle1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
										{courseDetailData.description}
									</Typography>

									<CourseStats>
										<StatItem>
											<AccessTimeIcon />
											<Typography variant="body2">12 Hours</Typography>
										</StatItem>

										<StatItem>
											<ArticleIcon />
											<Typography variant="body2">15 Lessons</Typography>
										</StatItem>

										<StatItem>
											<LanguageIcon />
											<Typography variant="body2">
												{courseDetailData.language === 'jp' ? "Japanese" : "English"}
											</Typography>
										</StatItem>
									</CourseStats>
								</HeroContent>
							</GlobalSpacing>
						</HeroSection>

						<GlobalSpacing>
							<ContentWrapper>
								<MainContent>
									<CourseSection>
										<Typography variant="h5">
											{t("courseDetails.aboutCourse")}
										</Typography>

										<Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
											{courseDetailData.description}
										</Typography>
									</CourseSection>

									<CourseSection>
										<Typography variant="h5">{t("courseDetails.whatYouWillLearn")}</Typography>

										<LearningPoints>
											{courseDetailData.learnOutcomes.map((data: string, key: number) => (
												<LearningPoint key={key}>
													<CheckIcon fontSize="small" />
													<Typography>{data}</Typography>
												</LearningPoint>
											))}
										</LearningPoints>
									</CourseSection>
								</MainContent>

								<SideContent>
									<CourseCard>
									<Elements stripe={stripePromise}>
									<CardContent>
										{!isUserPurchased ? ( // Conditionally render the button or message
											<FirstButton
												onClick={() => {
													setOpenSubscribeModal(true);
												}}
											>
												<Typography variant="body1">
													{t("buttons.subscribe")} ${courseDetailData.price}
												</Typography>
											</FirstButton>
										) : (
											<Typography variant="body1">
												{t("buttons.wait")}
											</Typography>
										)}

										<DocumentPayment
											data={courseDetailData}
											open={openSubscribeModal}
											onCancel={() => {
												setOpenSubscribeModal(false);
											}}
										/>
									</CardContent>
										</Elements>
									</CourseCard>
								</SideContent>
							</ContentWrapper>
						</GlobalSpacing>
					</CourseDetailsWrapper>

				</React.Fragment>
			)}
		</Layout>
	)
}

interface AnswerFormProps {
	QAdata: QuestionAnswersObject
	courseDetailData: CourseDetailObject
	setUpdateDetail: (state: boolean) => void
	updateDetail: boolean
}

const AnswerForm = ({ QAdata, courseDetailData, setUpdateDetail, updateDetail }: AnswerFormProps) => {
	const { t } = useI18n();
	const [answer, setAnswer] = useState("");
	const [errorAnswer, setErrorAnswer] = useState(false);
	const [, { dispatch }]: GlobalContextType = useGlobalContext();

	const onSubmitAnswer = async () => {
		try {
			if (!courseDetailData.isOwner) return false;

			if (!answer) {
				setErrorAnswer(false);
				return false;
			}

			dispatch({ loading: true });
			await restApi.submitAnswer({
				id: QAdata.id, courseId: courseDetailData.id, answer: answer,
			})

			setUpdateDetail(!updateDetail);
			dispatch({ loading: false });
			setAnswer(""); setErrorAnswer(false);
			toastMessage("success", "Answer submitted!");
		} catch (err: any) {
			dispatch({ loading: false });
			apiNotification(err, "Answer Submit failed!");
		}
	}

	return (
		<FormContainer>
			<TextField multiline rows={3}
				placeholder="Answer about this question..."
				onChange={({ target }: any) => { setAnswer(target.value); setErrorAnswer(false); }}
				error={errorAnswer}
				value={answer}
			/>

			<FirstButton onClick={onSubmitAnswer}>
				<Typography variant="body1">{t("buttons.answer")}</Typography>
				<SendIcon fontSize="small" />
			</FirstButton>
		</FormContainer>
	)
}

const CourseDetailsWrapper = styled(GlobalFirstSection)(({ theme }) => ({
	display: 'flex',
	flexDirection: "column",
	gap: theme.spacing(1),
	padding: `${theme.spacing(5)} 0px`,

	[theme.breakpoints.down("md")]: {
		padding: `${theme.spacing(3)} 0px`,
	}
}))

const HeroSection = styled(Box)(({ theme }) => ({
	backgroundColor: theme.palette.common.mainBg,
	borderBottom: `1px solid ${theme.palette.common.firstBorder}`,
	paddingBottom: theme.spacing(8),

	[theme.breakpoints.down("md")]: {
		paddingBottom: theme.spacing(7),
	}
}))

const HeroContent = styled(Box)(({ theme }) => ({
	maxWidth: '100%',
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(1)
}))

const CourseStats = styled(Box)(({ theme }) => ({
	display: 'flex',
	gap: theme.spacing(4),
	marginTop: theme.spacing(1),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
		marginTop: theme.spacing(0.5),
	}
}))

const StatItem = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1),
	color: `${theme.palette.common.firstColor}c0`,

	p: {
		color: `${theme.palette.common.firstColor}c0`
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(0.5),
	}
}))


const ContentWrapper = styled(Box)(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: '1fr 350px',
	gap: theme.spacing(3),
	marginTop: theme.spacing(-6),

	[theme.breakpoints.down('lg')]: {
		display: "flex",
		flexDirection: "column-reverse",
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
	}
}))

const MainContent = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(3),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(2),
	}
}))

const SideContent = styled(Box)(({ theme }) => ({
	top: 80,
	position: 'sticky',
	height: 'fit-content',

	[theme.breakpoints.down("lg")]: {
		position: "static",
	}
}))

const CourseCard = styled(Box)(({ theme }) => ({
	overflow: 'hidden',
	borderRadius: theme.spacing(1),
	backgroundColor: theme.palette.common.mainBg,
	boxShadow: `0px 1px 5px ${theme.palette.common.firstBorder}`,
}))

const CardContent = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	padding: theme.spacing(4),

	[theme.breakpoints.down("md")]: {
		padding: theme.spacing(1),
	}
}))


const CourseSection = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),

	padding: theme.spacing(3),
	borderRadius: theme.spacing(1),
	backgroundColor: theme.palette.common.mainBg,
	boxShadow: `0px 1px 5px ${theme.palette.common.firstBorder}`,

	h5: {
		fontWeight: 600,
	},

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
		padding: theme.spacing(2),
	}
}))

const LearningPoints = styled(Box)(({ theme }) => ({
	display: 'grid',
	gap: theme.spacing(2),
	gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))

const LearningPoint = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'flex-start',
	gap: theme.spacing(1),

	'& .MuiSvgIcon-root': {
		marginTop: '2px',
		color: theme.palette.common.thirdColor,
	},

	'& .MuiTypography-root': {
		color: `${theme.palette.common.firstColor}c0`,
	},
}))

const FormContainer = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	gap: theme.spacing(2),

	[theme.breakpoints.down("md")]: {
		gap: theme.spacing(1),
	}
}))


export { PDFDetail };